import {
    AccessTime,
    CalendarMonth,
    Delete,
    LocationOnOutlined,
    Person,
} from "@mui/icons-material";
import {
    Box,
    Button,
    Card,
    CardContent,
    CardMedia,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Typography,
} from "@mui/material";
import { useDispatch, useSelector } from "../../stores";

import { Lecture } from "../../typings/Lecture";
import { deleteLecture } from "../../stores/lectures/lecturesSlice";
import { formatDate } from "../../helpers/formatDate";
import { formatTime } from "../../helpers/formatTime";
import { selectAreaById } from "../../stores/areas/areasSelectors";
import { selectInstructorById } from "../../stores/instructors/instructorsSelectors";
import { selectTopicById } from "../../stores/topics/topicsSelectors";
import { styled } from "@mui/system";
import { useNavigate } from "react-router-dom";
import { useState } from "react";

type LectureCardProps = {
    lecture: Lecture;
    showDeleteButton?: boolean;
};

const LectureCard = ({
    lecture,
    showDeleteButton = false,
}: LectureCardProps) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);

    const topic = useSelector(selectTopicById(lecture.topic));
    const area = useSelector(selectAreaById(lecture.area));
    const instructor = useSelector(selectInstructorById(lecture.instructor));

    const hasDate = Boolean(lecture?.date);
    const hasTime = Boolean(lecture?.startTime) && Boolean(lecture?.endTime);
    const hasVideo = Boolean(lecture?.videoLink);


    return (
        <StyledCard onClick={() => navigate(`/lecture/${lecture.id}`)}>
            <Box position="relative">
                <CardMedia
                    component="img"
                    sx={{
                        height: 200,
                        borderTopRightRadius: 15,
                        borderTopLeftRadius: 15,
                        zIndex: 1,
                        objectFit: "cover",
                        width: "100%"
                    }}
                    image={lecture.image || "/assets/tawhadi_bg.png"}
                    title={lecture.title}
                    alt={lecture.title}
                />

                {showDeleteButton && (
                    <Box
                        onClick={(e) => {
                            e.stopPropagation();
                            setOpenDeleteConfirm(true);
                        }}
                        position="absolute"
                        top={8}
                        left={8}
                        zIndex={2}
                        sx={{
                            backgroundColor: "white",
                            borderRadius: "50%",
                            padding: "4px",
                            cursor: "pointer",
                        }}
                    >
                        <Delete style={{ color: "red" }} />
                    </Box>
                )}
            </Box>

            <CardContent
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "flex-start",
                    alignItems: "flex-end",
                }}
            >
                <Box
                    display="flex"
                    width="100%"
                    justifyContent="space-between"
                    alignItems="center"
                    gap={1}
                >
                    <Typography gutterBottom variant="h6" width="70%">
                        {lecture.title}
                    </Typography>

                    <Typography
                        gutterBottom
                        variant="subtitle1"
                        color="#a9a9a9"
                        width="30%"
                        textAlign="left"
                    >
                        {topic?.name || ""}
                    </Typography>
                </Box>

                <Box
                    display="flex"
                    width="100%"
                    justifyContent="space-between"
                    alignItems="center"
                >
                    <Typography
                        gutterBottom
                        variant="subtitle1"
                        color="#a9a9a9"
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        gap={0.5}
                    >
                         <Person
                            style={{ color: "#4F8CAD", fontSize: "18px" }}
                        />
                        
                        {instructor?.fullName || ""}
                       
                    </Typography>

                    {!hasVideo && (
                        <Typography
                            gutterBottom
                            variant="subtitle1"
                            color="#a9a9a9"
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            gap={0.5}
                        >
                            <LocationOnOutlined
                                style={{ color: "#4F8CAD", fontSize: "18px" }}
                            />
                            {area?.name || ""}
                        </Typography>
                    )}
                </Box>

                {(hasDate || hasTime) && (
                    <Box
                        display="flex"
                        width="100%"
                        justifyContent="space-between"
                        alignItems="center"
                    >
                        {hasDate && (
                            <Typography
                                gutterBottom
                                variant="subtitle1"
                                color="#a9a9a9"
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                                gap={0.5}
                            >
                                <CalendarMonth
                                    style={{ color: "#4F8CAD", fontSize: "18px" }}
                                />
                                {formatDate(lecture.date)}
                            </Typography>
                        )}

                        {hasTime && (
                            <Typography
                                gutterBottom
                                variant="subtitle1"
                                color="#a9a9a9"
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                                gap={0.5}
                            >
                                <AccessTime
                                    style={{ color: "#4F8CAD", fontSize: "18px" }}
                                />
                                {formatTime(lecture.startTime, lecture.date)} - {formatTime(lecture.endTime, lecture.date)}
                            </Typography>
                        )}
                    </Box>
                )}
            </CardContent>

            <Dialog
                open={openDeleteConfirm}
                onClose={() => setOpenDeleteConfirm(false)}
            >
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete this lecture?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={(e) => {
                            e.stopPropagation();
                            setOpenDeleteConfirm(false);
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        color="error"
                        onClick={(e) => {
                            e.stopPropagation();
                            dispatch(deleteLecture(lecture.id));
                            setOpenDeleteConfirm(false);
                        }}
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </StyledCard>
    );
};

export default LectureCard;

const StyledCard = styled(Card)(({ theme }) => ({
    justifyContent: "center",
    alignItems: "center",
    borderRadius: 15,
    border: `2px solid ${theme.palette.secondary.main}`,
    boxShadow: `1px 1px 0px #d3d3d3`,
    width: "99%",
    pointer: "cursor",
}));
